import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Map, FlaskConical, Microscope, Sparkles, AlertTriangle, Star,
} from "lucide-react";
import { getIllness } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import BodyDiagram from "../components/BodyDiagram";
import AtlasPageView from "../components/AtlasPageView";
import PdfCheckoutCard from "../components/PdfCheckoutCard";
import RuqyahRecommendation from "../components/RuqyahRecommendation";
import ShareOnWhatsApp from "../components/ShareOnWhatsApp";
import ShareMenu from "../components/ShareMenu";

const IllnessDetail = () => {
  const { illnessId } = useParams();
  const [illness, setIllness] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLanguage();

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    getIllness(illnessId, lang).then(setIllness).finally(() => setLoading(false));
  }, [illnessId, lang]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-cream rounded w-1/3" />
          <div className="h-64 bg-cream rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!illness) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-center">
        <p className="text-mute">{t("illness_not_found")}</p>
        <Link to="/" className="text-forest underline mt-4 inline-block">{t("home")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="illness-detail-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
      <Link to={`/category/${illness.category}`} data-testid="back-to-category" className="inline-flex items-center gap-2 text-mute hover:text-forest text-sm mb-8 transition">
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
        {t("back_to_category")}
      </Link>

      <div className="mb-10 max-w-4xl">
        <p data-testid="illness-arabic-name" className="arabic text-gold text-3xl mb-3">{illness.arabic_name}</p>
        <h1 data-testid="illness-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          {lang === "ar" ? illness.arabic_name : illness.name}
        </h1>
        <p className="text-mute leading-relaxed text-lg mb-5">{illness.summary}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <ShareOnWhatsApp title={lang === "ar" ? illness.arabic_name : illness.name} />
          <ShareMenu title={lang === "ar" ? illness.arabic_name : illness.name} compact />
        </div>
      </div>

      <div data-testid="page-disclaimer" className="mb-10 flex items-start gap-4 rounded-2xl border border-clay/30 bg-clay/5 p-5">
        <AlertTriangle className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" />
        <p className="text-sm text-clay leading-relaxed">{t("page_disclaimer")}</p>
      </div>

      {/* Hijama Atlas page (authoritative) — falls back to synthetic BodyDiagram only if no Atlas page exists */}
      <div className="mb-8">
        {illness.hijama_atlas_page ? (
          <AtlasPageView
            pageNumber={illness.hijama_atlas_page}
            illnessName={illness.name}
            atlasSource={illness.hijama_source || "Sheikh Ahmed Al-Hefni · Hijama Atlas"}
          />
        ) : (
          <BodyDiagram
            highlightedPoints={illness.hijama_points || []}
            illnessName={illness.name}
          />
        )}
      </div>

      {/* Premium PDF Checkout */}
      <PdfCheckoutCard illnessId={illness.id} illnessName={illness.name} />

      {/* Ruqyah recommendation */}
      <RuqyahRecommendation videos={illness.recommended_videos} />

      <div className="grid lg:grid-cols-3 gap-6">
        <section data-testid="quran-hadith-section" className="lg:col-span-2 clay-surface rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-2xl text-forest font-semibold">{t("section_revelation")}</h2>
          </div>

          {illness.quran_verses?.length > 0 && (
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t("label_quran")}</p>
              <div className="space-y-6">
                {illness.quran_verses.map((v, idx) => (
                  <div key={idx} data-testid={`quran-verse-${idx}`} className="border-l-2 border-gold pl-6 rtl:border-l-0 rtl:border-r-2 rtl:pl-0 rtl:pr-6">
                    <p className="arabic text-2xl text-ink mb-3">{v.arabic}</p>
                    <p className="text-mute italic mb-2">"{v.english}"</p>
                    <p className="text-xs text-forest font-medium">{t("label_surah")} {v.surah} · {v.reference}</p>
                    {v.relevance && (
                      <p className="text-xs text-mute italic mt-1.5">{v.relevance}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {illness.hadiths?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t("label_sahih_hadith")}</p>
              <div className="space-y-6">
                {illness.hadiths.map((h, idx) => (
                  <div key={idx} data-testid={`hadith-${idx}`} className="border-l-2 border-forest pl-6 rtl:border-l-0 rtl:border-r-2 rtl:pl-0 rtl:pr-6">
                    {h.arabic && <p className="arabic text-xl text-ink mb-3">{h.arabic}</p>}
                    <p className="text-mute italic mb-3">"{h.english}"</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="bg-forest text-sand px-3 py-1 rounded-full">{h.collection} · {h.reference}</span>
                      <span className="bg-cream text-forest px-3 py-1 rounded-full">{h.grading}</span>
                      {h.narrator && (
                        <span className="bg-cream text-mute px-3 py-1 rounded-full">{t("label_narrator")} {h.narrator}</span>
                      )}
                    </div>
                    {h.relevance && (
                      <p className="text-xs text-mute italic mt-2">{h.relevance}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {illness.classical_sources && (illness.classical_sources.al_qanun || illness.classical_sources.tibb_nabawi) && (
            <div className="mt-10 pt-8 border-t border-gold/30" data-testid="classical-sources">
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t("classical_sources_label")}</p>
              <div className="space-y-4">
                {illness.classical_sources.al_qanun && (
                  <div className="bg-cream/60 rounded-xl p-5" data-testid="al-qanun-ref">
                    <p className="text-xs uppercase tracking-wider text-clay mb-1.5 font-semibold">Ibn Sīnā · Al-Qānūn fī al-Ṭibb</p>
                    <p className="text-sm text-ink leading-relaxed">{illness.classical_sources.al_qanun}</p>
                  </div>
                )}
                {illness.classical_sources.tibb_nabawi && (
                  <div className="bg-cream/60 rounded-xl p-5" data-testid="tibb-nabawi-ref">
                    <p className="text-xs uppercase tracking-wider text-clay mb-1.5 font-semibold">Ibn al-Qayyim al-Jawziyyah · Al-Ṭibb an-Nabawī</p>
                    <p className="text-sm text-ink leading-relaxed">{illness.classical_sources.tibb_nabawi}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        <section data-testid="hijama-section" className="clay-surface-soft rounded-3xl p-7">
          <div className="flex items-center gap-3 mb-5">
            <Map className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-2xl text-forest font-semibold">{t("section_hijama_map")}</h2>
          </div>
          <p className="text-sm text-mute mb-5 leading-relaxed">{illness.hijama_notes}</p>
          <div className="space-y-2">
            {illness.hijama_points_detail?.map((pt) => (
              <Link
                key={pt.id}
                to={`/hijama-atlas#point-${pt.id}`}
                data-testid={`hijama-point-${pt.id}`}
                className="flex items-center justify-between gap-3 rounded-xl bg-white border border-forest/10 p-3 hover:border-forest/30 transition"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex-shrink-0 w-9 h-9 rounded-full bg-forest text-sand flex items-center justify-center text-xs font-semibold">{pt.id}</span>
                  <div className="min-w-0">
                    <p className="text-sm text-ink font-medium truncate">{lang === "ar" ? pt.arabic : pt.name}</p>
                    <p className="arabic text-xs text-gold truncate">{pt.arabic}</p>
                  </div>
                </div>
                {pt.is_sunnah && <Star className="w-4 h-4 text-gold flex-shrink-0" fill="currentColor" />}
              </Link>
            ))}
          </div>
          <p className="text-[10px] text-mute mt-5 italic">
            <Star className="w-3 h-3 text-gold inline mr-1" fill="currentColor" />
            {t("sunnah_note")}
          </p>
          {illness.hijama_source && (
            <div className="mt-4 pt-4 border-t border-gold/30 flex flex-wrap items-center gap-2" data-testid="hijama-atlas-citation">
              <span className="text-[10px] uppercase tracking-wider text-clay font-semibold">{t("source_label")}:</span>
              <span className="text-xs text-forest italic">{illness.hijama_source}</span>
              {illness.hijama_atlas_page && (
                <span className="text-[10px] text-mute">· {t("atlas_page_label")} {illness.hijama_atlas_page}</span>
              )}
            </div>
          )}
        </section>

        <section data-testid="pharmacy-section" className="lg:col-span-2 clay-surface rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <FlaskConical className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-2xl text-forest font-semibold">{t("section_pharmacy")}</h2>
          </div>
          <div className="space-y-5">
            {illness.prophetic_remedies?.map((r, idx) => (
              <article key={idx} data-testid={`remedy-${idx}`} className="rounded-2xl bg-cream/40 border border-forest/10 p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                  <h3 className="font-heading text-xl text-forest font-semibold">{r.name}</h3>
                  <p className="arabic text-gold text-lg">{r.arabic}</p>
                </div>
                <p className="text-sm text-mute leading-relaxed mb-3">
                  <strong className="text-ink">{t("label_preparation")}</strong> {r.preparation}
                </p>
                <p className="text-sm text-mute leading-relaxed mb-3">
                  <strong className="text-ink">{t("label_duration")}</strong> {r.duration}
                </p>
                <p className="text-xs text-forest italic">{t("label_source")} {r.source}</p>
              </article>
            ))}
          </div>
        </section>

        <section data-testid="lifestyle-section" className="clay-surface-soft rounded-3xl p-7">
          <div className="flex items-center gap-3 mb-5">
            <Sparkles className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-2xl text-forest font-semibold">{t("section_lifestyle")}</h2>
          </div>
          <ul className="space-y-3">
            {illness.lifestyle?.map((l, idx) => (
              <li key={idx} data-testid={`lifestyle-${idx}`} className="flex gap-3 text-sm text-mute leading-relaxed">
                <span className="text-gold mt-1">◆</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="studies-section" className="lg:col-span-3 clay-surface rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Microscope className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-2xl text-forest font-semibold">{t("section_studies")}</h2>
            <span className="text-xs text-mute italic">{t("studies_subtitle")}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {illness.modern_studies?.map((s, idx) => (
              <article key={idx} data-testid={`study-${idx}`} className="rounded-2xl bg-cream/40 border border-forest/10 p-6">
                <h3 className="font-heading text-lg text-forest font-semibold leading-snug mb-2">{s.title}</h3>
                <p className="text-xs text-mute mb-3">{s.authors} · <em>{s.journal}</em> ({s.year})</p>
                <p className="text-sm text-mute leading-relaxed mb-3">{s.findings}</p>
                {s.doi && <p className="text-xs text-forest font-mono">DOI: {s.doi}</p>}
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default IllnessDetail;
